<script lang="ts">
  import { onMount, tick } from "svelte";

  export interface SelectOption {
    value: string | number;
    label: string;
    description?: string;
    disabled?: boolean;
  }
  interface Props {
    value: string | number;
    options: readonly (SelectOption | string | number)[];
    id?: string;
    "aria-label"?: string;
    placeholder?: string;
    disabled?: boolean;
    class?: string;
    onchange?: (value: string | number) => void;
  }
  const uid = $props.id();
  let {
    value = $bindable(),
    options = [],
    id = `${uid}-trigger`,
    "aria-label": ariaLabel,
    placeholder = "Выберите…",
    disabled = false,
    class: extraClass = "",
    onchange,
  }: Props = $props();
  let open = $state(false);
  let active = $state(-1);
  let trigger = $state<HTMLButtonElement>();
  let popup = $state<HTMLUListElement>();
  let position = $state({ left: 0, top: 0, width: 0, height: 240, above: false });
  let search = "";
  let searchAt = 0;
  const normalized = $derived<SelectOption[]>(
    options.map((option) =>
      typeof option === "object" ? option : { value: option, label: String(option) },
    ),
  );
  const selected = $derived(normalized.find((option) => String(option.value) === String(value)));

  function place() {
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const height = Math.min(240, Math.max(below, above), normalized.length * 58 + 12);
    const width = Math.min(rect.width, window.innerWidth - 16);
    position = {
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      top: below >= height ? rect.bottom + 6 : rect.top - 6,
      width,
      height,
      above: below < height,
    };
  }
  function show() {
    if (disabled || !normalized.some((option) => !option.disabled)) {
      return;
    }
    active = normalized.findIndex(
      (option) => !option.disabled && String(option.value) === String(value),
    );
    if (active < 0) {
      active = normalized.findIndex((option) => !option.disabled);
    }
    search = "";
    place();
    open = true;
  }
  function choose(index: number) {
    const option = normalized[index];
    if (disabled || !option || option.disabled) {
      return;
    }
    value = option.value;
    open = false;
    trigger?.focus();
    onchange?.(option.value);
  }
  function move(direction: number) {
    for (let count = 1; count <= normalized.length; count++) {
      const index = (active + direction * count + normalized.length) % normalized.length;
      if (!normalized[index]?.disabled) {
        active = index;
        break;
      }
    }
  }
  function keydown(event: KeyboardEvent) {
    if (disabled) {
      return;
    }
    if (event.key === "Tab") {
      open = false;
      return;
    }
    if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        event.stopPropagation();
      }
      open = false;
      return;
    }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      if (!open) {
        show();
      } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        move(event.key === "ArrowDown" ? 1 : -1);
      }
      if (event.key === "Home") {
        active = normalized.findIndex((option) => !option.disabled);
      }
      if (event.key === "End") {
        active =
          normalized.length - 1 - [...normalized].reverse().findIndex((option) => !option.disabled);
      }
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (open) {
        choose(active);
      } else {
        show();
      }
      return;
    }
    const plainCharacter =
      event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (plainCharacter) {
      event.preventDefault();
      if (!open) {
        show();
      }
      const now = Date.now();
      search = now - searchAt > 700 ? event.key : search + event.key;
      searchAt = now;
      const term = search.toLocaleLowerCase();
      const index = normalized.findIndex(
        (option) => !option.disabled && option.label.toLocaleLowerCase().startsWith(term),
      );
      if (index >= 0) {
        active = index;
      }
    }
  }
  function portal(node: HTMLUListElement) {
    (trigger?.closest("dialog") ?? document.body).appendChild(node);
    return { destroy: () => node.remove() };
  }
  $effect(() => {
    if (disabled || !normalized.some((option) => !option.disabled)) {
      open = false;
    }
    if (open && (!normalized[active] || normalized[active].disabled)) {
      active = normalized.findIndex((option) => !option.disabled);
    }
  });
  $effect(() => {
    if (open && active >= 0) {
      const index = active;
      void tick().then(() => popup?.children[index]?.scrollIntoView({ block: "nearest" }));
    }
  });
  onMount(() => {
    function outside(event: Event) {
      const target = event.target as Node;
      if (!trigger?.contains(target) && !popup?.contains(target)) {
        open = false;
      }
    }
    function layout() {
      if (open) {
        place();
      }
    }
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", outside);
    window.addEventListener("resize", layout);
    window.addEventListener("scroll", layout, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("focusin", outside);
      window.removeEventListener("resize", layout);
      window.removeEventListener("scroll", layout, true);
    };
  });
</script>

<div class="custom-select {extraClass}">
  <button
    bind:this={trigger}
    {id}
    type="button"
    class="select-trigger"
    class:is-open={open}
    role="combobox"
    aria-label={ariaLabel}
    aria-expanded={open}
    aria-haspopup="listbox"
    aria-controls={`${uid}-list`}
    aria-activedescendant={open && active >= 0 ? `${uid}-option-${active}` : undefined}
    {disabled}
    onclick={() => (open ? (open = false) : show())}
    onkeydown={keydown}
  >
    <span class="trigger-label">{selected?.label ?? placeholder}</span>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"
      ><path d="m4 6 4 4 4-4" stroke="currentColor" stroke-width="1.5" /></svg
    >
  </button>
</div>
{#if open}
  <ul
    use:portal
    bind:this={popup}
    id={`${uid}-list`}
    class="select-dropdown"
    role="listbox"
    aria-label={ariaLabel}
    aria-labelledby={ariaLabel ? undefined : id}
    style:transform={position.above ? "translateY(-100%)" : undefined}
    style:left={`${position.left}px`}
    style:top={`${position.top}px`}
    style:width={`${position.width}px`}
    style:max-height={`${position.height}px`}
  >
    {#each normalized as option, index (option.value)}
      <li
        id={`${uid}-option-${index}`}
        class="select-item"
        class:is-active={active === index}
        role="option"
        tabindex="-1"
        aria-selected={String(option.value) === String(value)}
        aria-disabled={option.disabled || undefined}
        onpointerdown={(event) => event.preventDefault()}
        onpointermove={() => {
          if (!option.disabled) {
            active = index;
          }
        }}
        onclick={() => choose(index)}
        onkeydown={keydown}
      >
        <span class="item-copy"
          ><span>{option.label}</span>{#if option.description}<small>{option.description}</small
            >{/if}</span
        >
        {#if String(option.value) === String(value)}<span class="check" aria-hidden="true">✓</span
          >{/if}
      </li>
    {/each}
  </ul>
{/if}

<style>
  .custom-select {
    width: 100%;
    min-width: 0;
  }
  .select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    min-height: 2.8rem;
    padding: 0.65rem 0.85rem;
    border: 1px solid var(--line-strong);
    border-radius: 6px;
    color: var(--cream);
    background: var(--surface);
    font: inherit;
    font-size: 0.85rem;
    text-align: left;
    cursor: pointer;
    transition: border-color 150ms;
  }
  .select-trigger:hover:not(:disabled),
  .select-trigger.is-open {
    border-color: var(--mars-light);
  }
  .select-trigger:focus-visible {
    outline: 2px solid var(--mars-light);
    outline-offset: 3px;
  }
  .select-trigger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .trigger-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  svg {
    flex-shrink: 0;
    transition: transform 150ms;
  }
  .is-open svg {
    transform: rotate(180deg);
  }
  .select-dropdown {
    position: fixed;
    z-index: 10000;
    box-sizing: border-box;
    overflow-y: auto;
    overscroll-behavior: contain;
    margin: 0;
    padding: 5px;
    list-style: none;
    border: 1px solid var(--line-strong);
    border-radius: 8px;
    background: var(--surface-raised);
    box-shadow: 0 12px 32px rgb(0 0 0 / 0.4);
    font-family: var(--font-body);
    font-size: 0.85rem;
  }
  .select-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 0.75rem;
    border-radius: 4px;
    color: var(--cream);
    cursor: pointer;
  }
  .select-item.is-active {
    background: rgb(214 75 50 / 0.16);
  }
  .select-item[aria-selected="true"] {
    color: var(--white);
  }
  .select-item[aria-disabled="true"] {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .item-copy {
    display: grid;
    gap: 0.2rem;
    overflow-wrap: anywhere;
  }
  small {
    color: var(--muted);
    font-size: 0.75rem;
  }
  .check {
    color: var(--mars-light);
    flex-shrink: 0;
  }
</style>
